/**
 * Market strip (piyasa şeridi) — the five numbers that scroll across the top of
 * both apps: dolar, euro, sterlin, gram altın, BIST 100.
 *
 * This is display data and nothing else. It is read from `v_market_ticker`
 * (migration 20261102000001), which stands on its own table and never touches
 * `fx_rates` — see that migration's header for why the two are kept apart.
 * Nothing here may be used to convert money that ends up in a ledger; for that
 * there is `lookupFxRate` in api/accounting.ts.
 *
 * The row type is written out by hand rather than taken from `Tables<…>`,
 * because the strip has to compile and ship before anyone runs `db push` — the
 * generated types will not know this view until then. When the types are
 * regenerated the two should be reconciled.
 */
import type { AslenClient } from '../db';
import { formatNumber } from '../format';
import { unwrap } from './errors';

/** One symbol as the strip shows it. */
export interface MarketQuote {
  /** 'USD' | 'EUR' | 'GBP' | 'XAU_GRAM' | 'BIST100' — see `market_symbols` */
  symbol: string;
  label_tr: string;
  label_en: string;
  /** What follows the number; null for an index, which has no unit. */
  unit: string | null;
  decimals: number;
  sort: number;
  /** The quote's own day, not the day it was fetched. */
  date: string;
  value: number;
  source: 'TCMB' | 'EVDS' | 'MANUAL';
  /** The newest *earlier* day, so a Monday is measured against Friday. */
  prev_value: number | null;
  /** Null on the very first day of a symbol — the strip then shows no arrow. */
  change_pct: number | null;
}

/**
 * Every active symbol that has at least one quote, in display order.
 *
 * Returns `[]` rather than throwing when the view is missing: until the
 * migration is applied PostgREST answers 404 for an unknown relation, and a
 * decorative strip must not take the whole shell down with it. Any other error
 * is a real one and is rethrown.
 */
export async function listMarketTicker(client: AslenClient): Promise<MarketQuote[]> {
  const res = await (client as unknown as {
    from: (t: string) => {
      select: (c: string) => {
        order: (c: string) => Promise<{ data: MarketQuote[] | null; error: { code?: string; message: string } | null }>;
      };
    };
  })
    .from('v_market_ticker')
    .select('*')
    .order('sort');
  // PGRST205 = relation not found in the schema cache (migration not pushed yet)
  if (res.error && (res.error.code === 'PGRST205' || res.error.code === '42P01')) return [];
  return unwrap(res as never) as MarketQuote[];
}

/**
 * `1234.5678` → `"1.234,5678 ₺"`, or `"10.482,31"` for an index.
 *
 * Built on the house `formatNumber` rather than `Intl.NumberFormat`, for two
 * reasons. The repo formats every other number through that one function, with
 * its own separators and its own U+2212 minus; a second formatter would drift
 * from the first the day somebody changes a separator. And `Intl` is not a
 * given on the phone — Hermes ships it only when the build opts in, so an
 * `Intl` call here is a crash waiting for a release that turns it off.
 *
 * Not `formatMoney` either: these are quotes, not amounts in a currency the
 * company holds, and gram gold wants two decimals where a dollar rate wants
 * four. The locale stays Turkish even for an English reader — the strip quotes
 * the Turkish market, and `42,1538` is the number as it is published.
 */
export function formatQuote(q: Pick<MarketQuote, 'value' | 'decimals' | 'unit'>): string {
  const n = formatNumber(q.value, { decimals: q.decimals });
  return q.unit ? `${n} ${q.unit}` : n;
}

/**
 * `2.41` → `"+%2,41"`, `-0.3` → `"−%0,30"`, null → null.
 *
 * The sign goes before the `%`, which is why this cannot just be
 * `formatNumber(pct, { signed: true })` — that would read `%+2,41`. Minus is
 * U+2212 to match `formatNumber`'s own, not a hyphen.
 */
export function formatChange(pct: number | null): string | null {
  if (pct === null || !Number.isFinite(pct)) return null;
  const sign = pct > 0 ? '+' : pct < 0 ? '−' : '';
  return `${sign}%${formatNumber(Math.abs(pct), { decimals: 2 })}`;
}

/** 'up' | 'down' | 'flat' — what the arrow and the colour key off. */
export function changeDirection(pct: number | null): 'up' | 'down' | 'flat' {
  if (pct === null || !Number.isFinite(pct) || pct === 0) return 'flat';
  return pct > 0 ? 'up' : 'down';
}
